import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  AlertTriangle,
  Compass,
  DollarSign,
  Home as HomeIcon,
  Mail,
  Search,
  Settings2,
  Smartphone,
  UserCheck,
  Users,
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";

type Item = {
  id: string;
  group: "Pages" | "Creators" | "Recruiters" | "Candidates";
  label: string;
  hint?: string;
  icon: React.ReactNode;
  action: () => void;
};

export function CommandPalette() {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const navigate = useNavigate();

  // Cmd+P / Ctrl+P (palette) — Cmd+K is already used by RightRailChat.
  // Also `/` when not inside an input.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "p") {
        e.preventDefault();
        setOpen((o) => !o);
        return;
      }
      if (
        e.key === "/" &&
        !(e.target instanceof HTMLInputElement) &&
        !(e.target instanceof HTMLTextAreaElement) &&
        !(e.target as HTMLElement | null)?.isContentEditable
      ) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Lazy-load source data only while open (and cache).
  const creatorsQ = useQuery({
    queryKey: ["palette", "creators"],
    queryFn: () => api.creatorsList({ limit: 100 }),
    enabled: open,
    staleTime: 60_000,
  });
  const recruitersQ = useQuery({
    queryKey: ["palette", "recruiters"],
    queryFn: () => api.recruitersList({ limit: 100 }),
    enabled: open,
    staleTime: 60_000,
  });
  const candidatesQ = useQuery({
    queryKey: ["palette", "candidates"],
    queryFn: () => api.discoveryCandidates(),
    enabled: open,
    staleTime: 60_000,
  });

  const close = React.useCallback(() => {
    setOpen(false);
    setQ("");
  }, []);

  const go = React.useCallback(
    (path: string) => {
      navigate(path);
      close();
    },
    [navigate, close],
  );

  const pageItems: Item[] = React.useMemo(
    () => [
      { id: "p:home", group: "Pages", label: "Home", icon: <HomeIcon className="h-4 w-4" />, action: () => go("/") },
      { id: "p:triage", group: "Pages", label: "Triage", icon: <AlertTriangle className="h-4 w-4" />, action: () => go("/triage") },
      { id: "p:drafts", group: "Pages", label: "Drafts", icon: <Mail className="h-4 w-4" />, action: () => go("/drafts") },
      { id: "p:activity", group: "Pages", label: "Activity", icon: <Activity className="h-4 w-4" />, action: () => go("/activity") },
      { id: "p:creators", group: "Pages", label: "Creators", icon: <Users className="h-4 w-4" />, action: () => go("/creators") },
      { id: "p:recruiters", group: "Pages", label: "Recruiters", icon: <UserCheck className="h-4 w-4" />, action: () => go("/recruiters") },
      { id: "p:fleet", group: "Pages", label: "Fleet", icon: <Smartphone className="h-4 w-4" />, action: () => go("/fleet") },
      { id: "p:recruiting", group: "Pages", label: "Recruiting", icon: <Compass className="h-4 w-4" />, action: () => go("/recruiting") },
      { id: "p:costs", group: "Pages", label: "Costs", icon: <DollarSign className="h-4 w-4" />, action: () => go("/costs") },
      { id: "p:system", group: "Pages", label: "System", icon: <Settings2 className="h-4 w-4" />, action: () => go("/system") },
    ],
    [go],
  );

  const allItems: Item[] = React.useMemo(() => {
    const out: Item[] = [...pageItems];
    for (const c of creatorsQ.data?.items ?? []) {
      out.push({
        id: `c:${c.creator_id}`,
        group: "Creators",
        label: c.display_name || c.handle,
        hint: `@${c.handle} · ${c.status}`,
        icon: <Users className="h-4 w-4" />,
        action: () => go(`/creators/${encodeURIComponent(c.creator_id)}`),
      });
    }
    for (const r of recruitersQ.data ?? []) {
      out.push({
        id: `r:${r.recruiter_id}`,
        group: "Recruiters",
        label: r.name,
        hint: `${r.dms_yday ?? 0}/${r.daily_dm_quota ?? 30} yday`,
        icon: <UserCheck className="h-4 w-4" />,
        action: () => go("/recruiters"),
      });
    }
    for (const cand of candidatesQ.data ?? []) {
      const handle = cand.ig_handle || cand.source_handle;
      if (!handle) continue;
      out.push({
        id: `cand:${cand.candidate_id}`,
        group: "Candidates",
        label: handle,
        hint:
          (cand.uv_score != null ? `${cand.uv_score.toFixed(1)} · ` : "") +
          (cand.predicted_tier || cand.status),
        icon: <Compass className="h-4 w-4" />,
        action: () => go("/recruiting"),
      });
    }
    return out;
  }, [pageItems, creatorsQ.data, recruitersQ.data, candidatesQ.data, go]);

  const filtered = React.useMemo(() => {
    if (!q.trim()) return allItems;
    const s = q.toLowerCase();
    return allItems.filter((i) =>
      (i.label + " " + (i.hint ?? "")).toLowerCase().includes(s),
    );
  }, [allItems, q]);

  // Group results
  const grouped = React.useMemo(() => {
    const groups: Record<string, Item[]> = {};
    for (const it of filtered.slice(0, 50)) {
      (groups[it.group] ||= []).push(it);
    }
    return groups;
  }, [filtered]);

  // Keyboard nav
  const [activeIdx, setActiveIdx] = React.useState(0);
  React.useEffect(() => setActiveIdx(0), [q, open]);
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      const it = filtered[activeIdx];
      if (it) {
        e.preventDefault();
        it.action();
      }
    }
  };

  let runningIdx = -1;

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
      <DialogContent className="max-w-xl gap-0 p-0 overflow-hidden">
        <DialogTitle className="sr-only">Command palette</DialogTitle>
        <div className="flex items-center border-b px-3">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Jump to a creator, recruiter, candidate, or page…"
            className="flex h-11 w-full rounded-md bg-transparent px-2 py-3 text-sm outline-none placeholder:text-muted-foreground"
          />
          <kbd className="ml-2 hidden text-2xs text-muted-foreground md:inline">esc</kbd>
        </div>
        <div className="max-h-96 overflow-y-auto p-1">
          {filtered.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              No matches.
            </div>
          ) : (
            Object.entries(grouped).map(([groupName, items]) => (
              <div key={groupName} className="mb-1">
                <div className="px-2 pb-0.5 pt-2 text-2xs uppercase tracking-wide text-muted-foreground">
                  {groupName}
                </div>
                {items.map((it) => {
                  runningIdx += 1;
                  const isActive = runningIdx === activeIdx;
                  return (
                    <button
                      key={it.id}
                      onClick={it.action}
                      onMouseEnter={() => setActiveIdx(filtered.indexOf(it))}
                      className={
                        "flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left text-sm " +
                        (isActive ? "bg-accent text-accent-foreground" : "hover:bg-accent/60")
                      }
                    >
                      <span className="text-muted-foreground">{it.icon}</span>
                      <span className="flex-1 truncate">{it.label}</span>
                      {it.hint ? (
                        <span className="text-2xs text-muted-foreground">{it.hint}</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
        <div className="flex items-center justify-between border-t px-3 py-1.5 text-2xs text-muted-foreground">
          <span>↑↓ to nav · enter to jump</span>
          <span>⌘P or / toggles</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
